import { GeoJSON } from 'geojson'
import { capitalize } from 'lodash-es'

import { SearchResult } from '@/feature/map/hooks/useAutoCompleteLocationSearch'

import { TaskLocationPayload } from '../../api'
import { TaskLocation } from './TaskLocationPopout.types'

export enum GeoJsonType {
  Feature = 'Feature',
  Point = 'Point',
  LineString = 'LineString',
}

export const isLineString = (location: TaskLocation): boolean => {
  return location.geometryType === GeoJsonType.LineString
}

export const getLocationLabel = (location: TaskLocation): string => {
  if (location.type !== 'temporary') {
    return `${capitalize(location.type)} - ${location.name}`
  }
  if (location.geoJson) {
    return `Use as geo point "${location.name}"`
  }
  return location.address
}

export const buildGeoPointGeoJson = (lat: number, lng: number): GeoJSON => ({
  type: GeoJsonType.Feature,
  geometry: {
    type: GeoJsonType.Point,
    coordinates: [lng, lat],
  },
  properties: {
    radius: 100,
    home: { lat, lng },
  },
})

export const buildPlaceGeoJson = (
  geometryLocation: google.maps.LatLngLiteral,
): GeoJSON => ({
  type: GeoJsonType.Feature,
  geometry: {
    type: GeoJsonType.Point,
    coordinates: [geometryLocation.lng, geometryLocation.lat],
  },
  properties: {
    radius: 100,
    home: geometryLocation,
  },
})

export const getPlacesServiceDetails = (
  service: google.maps.places.PlacesService,
  placeId: string,
): Promise<google.maps.places.PlaceResult | null> => {
  return new Promise((resolve, reject) => {
    service.getDetails(
      { placeId, fields: ['name', 'geometry.location'] },
      (place, status) => {
        if (status === google.maps.places.PlacesServiceStatus.OK) {
          resolve(place)
        } else {
          reject(new Error(`PlacesService request failed with status: ${status}`))
        }
      },
    )
  })
}

export const formatSearchResultToTaskLocation = (geo: SearchResult): TaskLocation => ({
  id: geo.id,
  name: geo.description,
  address: geo.description,
  type: 'temporary',
  ...(geo.type === 'geoPoint' && {
    geoJson: buildGeoPointGeoJson(geo.location.lat, geo.location.lng),
  }),
})

export const buildEditPayloadForGeoPoint = (
  selectedLocation: TaskLocation,
): TaskLocationPayload => ({
  type: selectedLocation.type.toUpperCase(),
  name: selectedLocation.name,
  address: selectedLocation.name,
  geoJson: selectedLocation.geoJson,
})

export const buildEditPayloadForPlace = (
  selectedLocation: TaskLocation,
  place: google.maps.places.PlaceResult | null,
  geometryLocation: google.maps.LatLngLiteral,
): TaskLocationPayload => ({
  type: selectedLocation.type.toUpperCase(),
  name: place?.name ?? selectedLocation.address,
  address: selectedLocation.address,
  geoJson: buildPlaceGeoJson(geometryLocation),
})

export const buildEditPayloadForExisting = (
  selectedLocation: TaskLocation,
): TaskLocationPayload => ({
  type: selectedLocation.type.toUpperCase(),
  originalObjectId: selectedLocation.id!,
})
